import { ParamMap, Params } from '@angular/router';
import { APP } from 'src/app/config/app';
import { IBase, ISort } from 'src/app/models';
import {
  SortDirection,
  SuggestionSortType,
  SuggestionUserResolutionFilter,
  SuggestionVisibility,
} from 'src/app/models/enums';
import { SuggestionPriority } from 'src/app/models/enums/suggestionPriority';
import { SuggestionStatus } from 'src/app/models/enums/suggestionStatus';
import { SuggestionType } from 'src/app/models/enums/suggestionType';
import {
  parseEnumList,
  parseIdList,
  parseNumberList,
  serializeIdList,
  serializeNumberList,
  serializeStringList,
} from './filterUtils';

export const DEFAULT_SUGGESTION_SORT = SuggestionSortType.LAST_MODIFICATION;

function isSuggestionSortType(value: string | null | undefined): value is SuggestionSortType {
  return Object.values(SuggestionSortType).includes(value as SuggestionSortType);
}

function isSortDirection(value: string | null | undefined): value is SortDirection {
  return Object.values(SortDirection).includes(value as SortDirection);
}

export function resolveSuggestionsSort(
  params: ParamMap,
  storedSortType?: string | null,
  storedSortDirection?: string | null,
): ISort<SuggestionSortType> {
  const sortParam = params.get(APP.SUGGESTIONS_SORT_PARAM);
  const urlSortType = isSuggestionSortType(sortParam) ? sortParam : null;
  const type = urlSortType ?? (isSuggestionSortType(storedSortType) ? storedSortType : DEFAULT_SUGGESTION_SORT);
  const directionParam = params.get(APP.SUGGESTIONS_DIRECTION_PARAM);
  const fallbackDirection = urlSortType ? null : storedSortDirection;
  const direction = [directionParam, fallbackDirection].find(isSortDirection)
    ?? APP.SUGGESTIONS_DEFAULT_DIRECTIONS[type];
  return { type, direction };
}

export function serializeSuggestionsSort(sort: ISort<SuggestionSortType>): Params {
  const isDefaultDirection = sort.direction === APP.SUGGESTIONS_DEFAULT_DIRECTIONS[sort.type];
  const isDefaultSort = sort.type === DEFAULT_SUGGESTION_SORT && isDefaultDirection;
  return {
    [APP.SUGGESTIONS_SORT_PARAM]: isDefaultSort ? null : sort.type,
    [APP.SUGGESTIONS_DIRECTION_PARAM]: isDefaultDirection ? null : sort.direction,
  };
}

export interface ISuggestionsFilters {
  selectedClients: number[];
  selectedTowns: number[];
  selectedServices: IBase[];
  selectedTypes: SuggestionType[];
  selectedStatus: SuggestionStatus[];
  selectedPriorities: SuggestionPriority[];
  selectedUserResolutions: SuggestionUserResolutionFilter[];
  selectedVisibilities: SuggestionVisibility[];
  selectedExpeditions: IBase[];
}

export type ISuggestionsPanelFilters = Pick<
  ISuggestionsFilters,
  'selectedClients' | 'selectedTowns' | 'selectedServices' | 'selectedExpeditions'
>;

export function toSuggestionsPanelFilters(filters: ISuggestionsFilters): ISuggestionsPanelFilters {
  return {
    selectedClients: filters.selectedClients,
    selectedTowns: filters.selectedTowns,
    selectedServices: filters.selectedServices,
    selectedExpeditions: filters.selectedExpeditions,
  };
}

export const SUGGESTION_FILTER_KEYS = [
  'clients', 'towns', 'services', 'types', 'status', 'priorities', 'resolutions', 'visibilities', 'expeditions'
] as const;

export function emptySuggestionsFilters(): ISuggestionsFilters {
  return toSuggestionsFilters({});
}

export function toSuggestionsFilters(source: Partial<Record<keyof ISuggestionsFilters, unknown>>): ISuggestionsFilters {
  const listOf = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);
  return {
    selectedClients: listOf(source.selectedClients),
    selectedTowns: listOf(source.selectedTowns),
    selectedServices: listOf(source.selectedServices),
    selectedTypes: listOf(source.selectedTypes),
    selectedStatus: listOf(source.selectedStatus),
    selectedPriorities: listOf(source.selectedPriorities),
    selectedUserResolutions: listOf(source.selectedUserResolutions),
    selectedVisibilities: listOf(source.selectedVisibilities),
    selectedExpeditions: listOf(source.selectedExpeditions),
  };
}

export function serializeSuggestionsFilters(f: ISuggestionsFilters): Params {
  return {
    clients: serializeNumberList(f.selectedClients),
    towns: serializeNumberList(f.selectedTowns),
    services: serializeIdList(f.selectedServices),
    types: serializeStringList(f.selectedTypes),
    status: serializeStringList(f.selectedStatus),
    priorities: serializeStringList(f.selectedPriorities),
    resolutions: serializeStringList(f.selectedUserResolutions),
    visibilities: serializeStringList(f.selectedVisibilities),
    expeditions: serializeIdList(f.selectedExpeditions),
  };
}

export function deserializeSuggestionsFilters(
  params: ParamMap,
  fallback?: ISuggestionsFilters
): ISuggestionsFilters {
  return {
    selectedClients: parseNumberList(params.get('clients')),
    selectedTowns: parseNumberList(params.get('towns')),
    selectedServices: parseIdList(params.get('services'), fallback?.selectedServices),
    selectedTypes: parseEnumList<SuggestionType>(params.get('types')),
    selectedStatus: parseEnumList<SuggestionStatus>(params.get('status')),
    selectedPriorities: parseEnumList<SuggestionPriority>(params.get('priorities')),
    selectedUserResolutions: parseEnumList<SuggestionUserResolutionFilter>(params.get('resolutions')),
    selectedVisibilities: parseEnumList<SuggestionVisibility>(params.get('visibilities')),
    selectedExpeditions: parseIdList(params.get('expeditions'), fallback?.selectedExpeditions),
  };
}

export function parseSuggestionsFiltersFromStorage(json: string | null | undefined): ISuggestionsFilters | null {
  if (!json) return null;
  try {
    return toSuggestionsFilters(JSON.parse(json));
  } catch {
    return null;
  }
}
